import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Grid, Typography, Snackbar, Alert, Tooltip,
} from '@mui/material';
import { Field } from '../../UI';
import {
  buscarIsUsuarioAssistente, buscarTipoProcessoContatos, inserirNovoAnotacao, inserirNovoHistorico,
} from '../../../../services/core/processos/processosService';
import {
  buscarOcorrenciasGrupoElfa, registrarOcorrenciaGrupoElfa,
} from '../../../../services/core/processos/grupoElfaOcorrenciasService';
import { buildDefaultSelector } from '../../../../store/Global/LoadSelectors/loadSelectors.factory';
import { tiposHistoricos } from '../../../../types/tiposHistoricoTypes';
import { tipoTextoPadraoTypes } from '../../../../types/tipoTextoPadraoTypes';
import {
  clearErrorField, extractResponseErrorMessage, makeErrorField, updateFieldsWithErrors,
} from '../../../../helpers/utils/form.utils';
import { haveExceptionValidations, isBadRequestException } from '../../../../helpers/utils/utils';
import * as C from '../../../theme/colors.constants';
import DialogTextoPadrao from './DialogTextoPadrao';
import DialogReescrever from './DialogReescrever';
import DialogSelecionarAntecedentes from './DialogSelecionarAntecedentes';
import usePodeAtuarNaFase from '../../../hooks/usePodeAtuarNaFase';

const MENSAGEM_SELECIONAR_ANTECEDENTES = 'Selecione os antecedentes que também devem receber esta anotação/relatório:';

const TIPOS_TEXTO_PADRAO_ANOTACAO = [
  tipoTextoPadraoTypes.HISTORICO.value, tipoTextoPadraoTypes.DEVOLUCAO.value,
];
const TIPOS_TEXTO_PADRAO_RELATORIO = [tipoTextoPadraoTypes.RELATORIO.value];

const MENSAGEM_ERRO_GENERICA = 'Não foi possível salvar a anotação.';
const MINIMO_PALAVRAS_REESCREVER = 5;
const DICA_MINIMO_PALAVRAS = 'São necessárias no mínimo 5 palavras para reescrever o texto.';

// Mesma fórmula da tela real (`AcaoAnotar.jsx`): `trim().split(' ').length`,
// sem filtrar espaços múltiplos — replicado por paridade de comportamento
// observável (mesmo limiar de habilitação que o usuário já conhece da tela
// real), não uma contagem de palavras "correta" de propósito geral.
function contarPalavras(texto) {
  return texto.trim().split(' ').length;
}

// Defaults replicados da tela real (`AcaoAnotar.jsx`): tipo de contato "4"
// e tipo de histórico "7", ambos literais sem constante nomeada na fonte
// real. `TIPO_HISTORICO_PADRAO` é Number aqui (diferente do `'7'` string da
// tela real) porque `tiposHistoricoOptions` vem de `buildDefaultSelector`
// (id numérico) — um default string quebraria o match inicial do `Field`
// tipo=select contra as options.
const TIPO_CONTATO_PADRAO = 4;
const TIPO_HISTORICO_PADRAO = 7;
const IDS_TIPO_HISTORICO_VISIVEIS = [TIPO_HISTORICO_PADRAO, 9, 10];

// Tipos de histórico que dispensam a exigência de o texto ter passado pelo
// "Reescrever" antes de habilitar "Anotar" — mesmos ids literais (9, 10) já
// usados em `habilitaConcluir()` da tela real (`AcaoAnotar.jsx`), nomeados
// aqui para ficar auto-explicativo.
const IDS_TIPO_HISTORICO_DISPENSA_REESCREVER = [9, 10];

const OCORRENCIA_NENHUMA = { id: '', value: 'Nenhuma', complementoObrigatorio: false };
const MENSAGEM_ERRO_GRUPO_ELFA = 'Não foi possível verificar o status Elfa do processo. O bloco de Ocorrência seguirá oculto.';

function makeEstadoInicial() {
  return {
    tipoProcessoContatoSelecionado: TIPO_CONTATO_PADRAO,
    ddd: '',
    telefone: '',
    nomeContatoRecado: '',
    idTipoHistorico: TIPO_HISTORICO_PADRAO,
    textoAnotacao: '',
    textoRelatorio: '',
  };
}

// Mesmo shape de `acaoAnotarController.makeDefaultErrorsFields()` (tela
// real): `texto` é UM único campo, compartilhado pelas 2 chamadas de
// serviço (`inserirNovoAnotacao`/`inserirNovoHistorico`) — a API não
// diferencia de qual das duas veio um erro de validação em "texto", e a
// tela real também não tenta diferenciar (mesmo `errorsFields.texto`
// exibido nos dois textareas). Replicado aqui por paridade, não uma
// limitação nova introduzida por este componente.
function makeErrorFieldsIniciais() {
  return {
    tipoProcessoContatoSelecionado: makeErrorField(),
    ddd: makeErrorField(),
    telefone: makeErrorField(),
    nomeContatoRecado: makeErrorField(),
    idTipoHistorico: makeErrorField(),
    texto: makeErrorField(),
  };
}

/**
 * Bloco de Anotação — Etapas 5/6/7 do plano `docs/architecture/
 * plano-aw2-1054-aba-historico.md`. Port de `AcaoAnotar.jsx` real, mas
 * sempre visível na sidebar (não mais um painel condicional) e SEM
 * replicação em antecedentes selecionados: decisão do usuário nesta sessão
 * — a grid "Antecedentes Ativos" (Etapa 4) é só consulta, sem seleção. Por
 * isso o escopo aqui é só o processo selecionado, mais enxuto que a tela
 * real (sem Grupo Elfa/Ocorrências, sem dialog de seleção de antecedentes).
 * Textos Padrão (Etapa 6, `DialogTextoPadrao.jsx`) e Reescrever/IA (Etapa 7,
 * `DialogReescrever.jsx`) já integrados, um sub-dialog por campo e por
 * função (Anotação/Relatório × Texto Padrão/Reescrever).
 *
 * DDD/Telefone são `Field` de texto simples, sem a máscara dinâmica por
 * dígito da tela real (`NumberInput`/`MaskField`) — mesma simplificação já
 * aceita em `DialogTitulo.jsx` para campos monetários (protótipo não tem
 * `FieldModel`/máscara real).
 *
 * Serviço chamado direto daqui (não do container), mesmo padrão de
 * `DialogTitulo.jsx` — `onSucesso` é a única ponte com o container, usada
 * só para recarregar `historicos` (Etapa 2).
 */
export default function BlocoAnotacao({
  processo, sinalizacaoInfo, antecedentesAtivos, tiposHistoricoOptions, onSucesso, deveResetarCampos,
}) {
  const [campos, setCampos] = useState(makeEstadoInicial);
  const [tipoProcessoContatos, setTipoProcessoContatos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [errorFields, setErrorFields] = useState(makeErrorFieldsIniciais);
  const [textoPadraoAberto, setTextoPadraoAberto] = useState(null);
  const [reescreverAberto, setReescreverAberto] = useState(null);
  const [antecedentesSelecionados, setAntecedentesSelecionados] = useState([]);
  const [antecedentesDialogAberto, setAntecedentesDialogAberto] = useState(false);

  // `reescreveuAnotacao`/`reescreveuRelatorio`: true logo após aceitar uma
  // reescrita via IA (`DialogReescrever.jsx`), false assim que o usuário
  // edita o texto manualmente de novo — port de `habilitaConcluir()` real.
  const [reescreveuAnotacao, setReescreveuAnotacao] = useState(false);
  const [reescreveuRelatorio, setReescreveuRelatorio] = useState(false);

  // Bloco "Ocorrência" (Grupo Elfa) — port de `AcaoAnotar.jsx` real.
  const [isUsuarioAssistente, setIsUsuarioAssistente] = useState(false);
  const [ehGrupoElfa, setEhGrupoElfa] = useState(false);
  const [ocorrenciasDisponiveis, setOcorrenciasDisponiveis] = useState([]);
  const [codOcorrencia, setCodOcorrencia] = useState('');
  const [complementoObrigatorio, setComplementoObrigatorio] = useState(false);
  const [complemento, setComplemento] = useState('');
  // Snackbar separado do `erro` (severity="error", usado pelo submit) de
  // propósito — mesma regra já documentada em `NovoAcordoPassoTres.jsx`:
  // reaproveitar o state de um Snackbar com efeito colateral no `onClose`
  // (aqui, `erro` não tem efeito colateral próprio, mas é semanticamente o
  // canal de "falha de ação do usuário", não de "aviso de fetch em
  // background") para uma falha de rede transitória e não-bloqueante
  // confundiria as duas origens.
  const [avisoNaoBloqueante, setAvisoNaoBloqueante] = useState('');

  // Dado de referência global (não por-processo) — mesmo padrão de fetch
  // único no mount já usado para `fasesIds`/`parametrosInternosAbe`.
  useEffect(() => {
    buscarTipoProcessoContatos().then(({ data }) => {
      setTipoProcessoContatos(buildDefaultSelector(data));
    }).catch(() => {});
  }, []);

  // `isUsuarioAssistente` é propriedade do usuário LOGADO (o service não
  // recebe `idProcesso`), não do processo selecionado — buscado uma única
  // vez no mount. A tela real rebusca a cada troca de processo (efeito com
  // deps `[processo, ...]`) sem necessidade real, já que o valor nunca muda
  // entre processos na mesma sessão — simplificação consciente.
  useEffect(() => {
    buscarIsUsuarioAssistente().then(({ data }) => {
      setIsUsuarioAssistente(data === 'S');
    }).catch(() => {});
  }, []);

  // Bloco Ocorrência: fetch por-processo, mesmo padrão já obrigatório no
  // módulo (reset síncrono de TODO estado derivado no início do efeito +
  // guard `ignore` no cleanup) — nunca deixar resposta tardia de um
  // processo trocado sobrescrever o estado do processo atual.
  //
  // Deps só `[processo?.id]`, NUNCA `deveResetarCampos` — diferente do
  // reset de `campos`/`reescreveu*` (que só reseta quando o usuário pediu,
  // via checkbox "Limpar campos"). Os códigos de Ocorrência disponíveis
  // pertencem ao processo/cliente atual — preservar uma seleção entre
  // processos, mesmo com "Limpar campos" desmarcado, arriscaria enviar um
  // `codOcorrencia` que nem existe (ou significa outra coisa) para o
  // processo novo. Decisão deliberada, não uma omissão.
  useEffect(() => {
    setEhGrupoElfa(false);
    setOcorrenciasDisponiveis([]);
    setCodOcorrencia('');
    setComplementoObrigatorio(false);
    setComplemento('');

    const alvo = processo?.id;
    if (!alvo) return undefined;

    let ignore = false;
    buscarOcorrenciasGrupoElfa(alvo).then(({ data }) => {
      if (ignore) return;
      setEhGrupoElfa(data.ehGrupoElfa === true);
      setOcorrenciasDisponiveis([
        OCORRENCIA_NENHUMA,
        ...(data.ocorrencias || []).map(o => ({
          id: o.cod,
          value: `${o.cod} — ${o.descricao}`,
          complementoObrigatorio: o.complementoObrigatorio === true,
        })),
      ]);
    }).catch(() => {
      // Fail-closed (mantém `ehGrupoElfa=false`, já setado no reset síncrono
      // acima) + aviso não-bloqueante — nunca silencioso: sem isso, uma
      // falha de rede transitória faria o bloco Ocorrência simplesmente
      // desaparecer para um cliente Grupo Elfa, sem nenhum feedback de que
      // a exigência não foi cumprida por indisponibilidade, e não por
      // não-aplicabilidade (mesmo padrão já corrigido em
      // `NovoAcordoPassoTres.jsx` para este mesmo service).
      if (ignore) return;
      setEhGrupoElfa(false);
      setAvisoNaoBloqueante(MENSAGEM_ERRO_GRUPO_ELFA);
    });
    return () => { ignore = true; };
  }, [processo?.id]);

  // Identificador de sessão monotônico — mesmo padrão de `DialogTitulo.jsx`
  // (`sessaoRef`). Uma comparação simples "idProcesso ainda é o mesmo" não
  // basta aqui: se o usuário sair do processo A (submit em voo) e VOLTAR a A
  // antes da resposta chegar, `processo?.id === alvo` volta a ser true, mas
  // a resposta é de uma tentativa antiga cujos campos já foram resetados
  // (possivelmente re-digitados) nesse meio-tempo — aplicar sucesso/erro
  // dela sobrescreveria o que o usuário já recomeçou a digitar para A.
  // Incrementar a cada troca de processo (não só comparar o id atual)
  // invalida corretamente essa resposta tardia mesmo no cenário A→B→A.
  const sessaoRef = useRef(0);
  const enviandoRef = useRef(false);

  // Reset ao trocar de processo — Etapa 10 do plano: `deveResetarCampos`
  // (checkbox "Limpar campos" marcado, OU primeira visita a este processo
  // nesta sessão — calculado no container) decide se `campos` (o texto
  // efetivamente digitado pelo usuário) é limpo ou preservado. Estado
  // técnico/transitório (guard de sessão, flag de envio em voo, erro de uma
  // tentativa anterior, sub-dialogs abertos) sempre reseta, incondicional —
  // nunca faz sentido continuar "enviando" ou mostrar erro/dialog de um
  // processo que não é mais o selecionado, independente do que o usuário
  // pediu para preservar.
  const idProcessoAnteriorRef = useRef(processo?.id);
  useEffect(() => {
    if (idProcessoAnteriorRef.current !== processo?.id) {
      sessaoRef.current += 1;
      setErro('');
      setAvisoNaoBloqueante('');
      setErrorFields(makeErrorFieldsIniciais());
      setTextoPadraoAberto(null);
      setReescreverAberto(null);
      setAntecedentesDialogAberto(false);
      enviandoRef.current = false;
      setEnviando(false);
      if (deveResetarCampos) {
        setCampos(makeEstadoInicial());
        setAntecedentesSelecionados([]);
        // Tratadas como parte do "texto digitado" (qualificam se o texto
        // preservado já passou por reescrita), não como estado puramente
        // técnico — resetam junto com `campos`, não incondicionalmente.
        setReescreveuAnotacao(false);
        setReescreveuRelatorio(false);
      }
      idProcessoAnteriorRef.current = processo?.id;
    }
  }, [processo?.id, deveResetarCampos]);

  // Reseed (não reset) — `antecedentesAtivos` pode mudar de referência SEM
  // o processo em foco trocar (Unificar/Juntar em Acompanhamento altera a
  // mesma lista compartilhada). Sem isso, um id removido e depois
  // reintroduzido (ex. Unificar desfeito) reapareceria marcado sem o
  // usuário reselecionar — mesmo padrão já usado em `MudancaFaseEnvio.jsx`.
  // O pré-filtro em `onSubmit` já impede uma replicação real com um id
  // obsoleto; este efeito só evita a seleção "fantasma" ficar visível.
  useEffect(() => {
    setAntecedentesSelecionados((atual) => {
      if (!antecedentesAtivos) return atual;
      const idsValidos = new Set(antecedentesAtivos.map(a => a.id));
      const filtrado = atual.filter(id => idsValidos.has(id));
      return filtrado.length === atual.length ? atual : filtrado;
    });
  }, [antecedentesAtivos]);

  const setCampo = nome => (event) => {
    const { value } = event.target;
    setCampos(atual => ({ ...atual, [nome]: value }));
  };

  // Edição manual do texto invalida uma reescrita anterior (port de
  // `onChangeTextoAnotacao`/`onChangeTextoRelatorio` reais) — só estes 2
  // campos precisam desse efeito colateral, por isso não usam `setCampo`.
  function onChangeTextoAnotacao(event) {
    const { value } = event.target;
    if (reescreveuAnotacao) setReescreveuAnotacao(false);
    setCampos(atual => ({ ...atual, textoAnotacao: value }));
  }

  function onChangeTextoRelatorio(event) {
    const { value } = event.target;
    if (reescreveuRelatorio) setReescreveuRelatorio(false);
    setCampos(atual => ({ ...atual, textoRelatorio: value }));
  }

  function limparErro(nome) {
    return () => setErrorFields(atual => clearErrorField(atual, nome));
  }

  function onSelectOcorrencia(event) {
    const cod = event.target.value;
    setCodOcorrencia(cod);
    const ocorrencia = ocorrenciasDisponiveis.find(o => String(o.id) === String(cod));
    setComplementoObrigatorio(ocorrencia?.complementoObrigatorio === true);
    setComplemento('');
  }

  function onToggleAntecedente(id) {
    setAntecedentesSelecionados(atual => (
      atual.includes(id) ? atual.filter(item => item !== id) : [...atual, id]
    ));
  }

  function onSelecionarTodosAntecedentes(marcarTodos) {
    setAntecedentesSelecionados(marcarTodos ? (antecedentesAtivos || []).map(a => a.id) : []);
  }

  function onCancelarSelecaoAntecedentes() {
    setAntecedentesSelecionados([]);
    setAntecedentesDialogAberto(false);
  }

  // Preserva o que já estava digitado (critério de pronto da Etapa 6) sem o
  // espaço solto no início que a tela real produz quando o campo começa
  // vazio (`${'' } ${texto}`) — divergência de correção pura, não de
  // paridade (ver `ResumoAntecedenteAtual.jsx`/AW2-1033 no CLAUDE.md do
  // módulo: bug cosmético sem service dependendo do resultado não precisa
  // ser replicado).
  function concatenarTextoPadrao(nomeCampo) {
    return (textoPadrao) => {
      setCampos(atual => ({
        ...atual,
        [nomeCampo]: atual[nomeCampo].trim().length > 0
          ? `${atual[nomeCampo]} ${textoPadrao}`
          : textoPadrao,
      }));
    };
  }

  function substituirTexto(nomeCampo) {
    return (novoTexto) => {
      setCampos(atual => ({ ...atual, [nomeCampo]: novoTexto }));
      if (nomeCampo === 'textoAnotacao') setReescreveuAnotacao(true);
      if (nomeCampo === 'textoRelatorio') setReescreveuRelatorio(true);
    };
  }

  const podeReescreverAnotacao = contarPalavras(campos.textoAnotacao) >= MINIMO_PALAVRAS_REESCREVER;
  const podeReescreverRelatorio = contarPalavras(campos.textoRelatorio) >= MINIMO_PALAVRAS_REESCREVER;

  const tipoHistoricoSelectorItems = useMemo(
    () => tiposHistoricoOptions.filter(tipo => IDS_TIPO_HISTORICO_VISIVEIS.includes(tipo.id)),
    [tiposHistoricoOptions],
  );

  // Port 1:1 de `habilitaConcluir()` da tela real (`AcaoAnotar.jsx`) —
  // decisão explícita do usuário: paridade EXATA, sem o gate adicional
  // `campos.idTipoHistorico !== ''` que uma sessão anterior deste protótipo
  // havia introduzido (a tela real confia só na validação de backend para
  // esse campo, já coberta por `errorFields.idTipoHistorico` desde a Etapa
  // 2 da AW2-1055) — lacuna conhecida e aceita conscientemente, não um
  // esquecimento.
  const idTipoHistoricoDispensaReescrever = IDS_TIPO_HISTORICO_DISPENSA_REESCREVER
    .includes(Number(campos.idTipoHistorico));

  const podeSubmeterOcorrencia = !ehGrupoElfa
    || codOcorrencia === ''
    || (!complementoObrigatorio || complemento.trim().length > 0);

  // Ramo `isUsuarioAssistente`: exige AMBOS os campos preenchidos, e o
  // Relatório sempre precisa ter sido reescrito; a Anotação só dispensa
  // reescrita quando `idTipoHistoricoDispensaReescrever`.
  const podeSubmeterComoAssistente = (idTipoHistoricoDispensaReescrever
    && campos.textoAnotacao.trim().length > 0
    && campos.textoRelatorio.trim().length > 0
    && reescreveuRelatorio)
    || (campos.textoRelatorio.trim().length > 0
      && campos.textoAnotacao.trim().length > 0
      && reescreveuRelatorio
      && reescreveuAnotacao);

  // Ramo padrão (não-assistente): um único campo preenchido já basta quando
  // `idTipoHistoricoDispensaReescrever`; caso contrário, o campo preenchido
  // precisa ter sido reescrito (ou ambos, se ambos estiverem preenchidos).
  const podeSubmeterComoNaoAssistente = (idTipoHistoricoDispensaReescrever
    && (campos.textoAnotacao.trim().length > 0 || campos.textoRelatorio.trim().length > 0))
    || (campos.textoRelatorio.trim().length === 0
      && campos.textoAnotacao.trim().length > 0 && reescreveuAnotacao)
    || (campos.textoAnotacao.trim().length === 0
      && campos.textoRelatorio.trim().length > 0 && reescreveuRelatorio)
    || (campos.textoRelatorio.trim().length > 0 && campos.textoAnotacao.trim().length > 0
      && reescreveuRelatorio && reescreveuAnotacao);

  const podeSubmeter = podeSubmeterOcorrencia
    && (isUsuarioAssistente ? podeSubmeterComoAssistente : podeSubmeterComoNaoAssistente);

  // AW2-1055 Etapa 1: gate client-side (só UX, sem mensagem de bloqueio —
  // AC pede "desabilitado, não oculto, sem mensagem") por permissão de fase
  // do usuário logado. AW2-1056 Etapa 1: extraído para o hook compartilhado
  // `usePodeAtuarNaFase` (4ª ocorrência do padrão no módulo, junto de
  // `MudancaFaseEnvio.jsx`) — mesma lógica fail-closed de antes, sem mudança
  // de comportamento. Autorização real permanece 100% no backend
  // (`inserirNovoAnotacao`/`inserirNovoHistorico`), este gate nunca a
  // substitui.
  const idFaseAtual = sinalizacaoInfo?.idFase != null ? Number(sinalizacaoInfo.idFase) : null;
  const podeAnotarNaFase = usePodeAtuarNaFase(idFaseAtual);

  async function onSubmit() {
    const alvo = processo?.id;
    if (!alvo || enviandoRef.current || !podeSubmeter || !podeAnotarNaFase) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErro('');
    setErrorFields(makeErrorFieldsIniciais());

    // Revalida a seleção contra a lista ATUAL de antecedentes ativos antes
    // de qualquer chamada — nunca confiar que ids selecionados anteriormente
    // ainda pertencem à lista atual (podem ter mudado de fase, sido
    // unificados/juntados em Acompanhamento, sem o processo em foco ter
    // mudado). `antecedentesAtivos == null` (ainda não carregado) resulta em
    // nenhuma replicação — fail-closed, nunca tratado como "sem restrição".
    const antecedentesValidos = antecedentesSelecionados.filter(
      id => (antecedentesAtivos || []).some(a => a.id === id),
    );

    try {
      if (campos.textoAnotacao.trim().length > 0) {
        await inserirNovoAnotacao(
          alvo,
          campos.idTipoHistorico,
          campos.textoAnotacao.trim(),
          campos.tipoProcessoContatoSelecionado,
          campos.ddd,
          campos.telefone,
          campos.nomeContatoRecado,
        );
        if (antecedentesValidos.length > 0) {
          await Promise.allSettled(antecedentesValidos.map(id => inserirNovoAnotacao(
            id,
            campos.idTipoHistorico,
            campos.textoAnotacao.trim(),
            campos.tipoProcessoContatoSelecionado,
            campos.ddd,
            campos.telefone,
            campos.nomeContatoRecado,
          )));
        }
      }
      if (campos.textoRelatorio.trim().length > 0) {
        await inserirNovoHistorico(alvo, tiposHistoricos.RELATORIO.key, campos.textoRelatorio.trim());
        if (antecedentesValidos.length > 0) {
          await Promise.allSettled(antecedentesValidos.map(
            id => inserirNovoHistorico(id, tiposHistoricos.RELATORIO.key, campos.textoRelatorio.trim()),
          ));
        }
      }
      if (ehGrupoElfa && codOcorrencia !== '') {
        await registrarOcorrenciaGrupoElfa(alvo, { codOcorrencia, complemento: complemento.trim() });
      }

      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      // Mesmo comportamento da tela real: só texto/tipo de histórico/
      // ocorrência resetam após o sucesso — contato/DDD/telefone/nome
      // persistem para permitir múltiplas anotações seguidas com o mesmo
      // contato.
      setCodOcorrencia('');
      setComplementoObrigatorio(false);
      setComplemento('');
      setCampos(atual => ({
        ...atual, textoAnotacao: '', textoRelatorio: '', idTipoHistorico: '',
      }));
      if (onSucesso) {
        onSucesso();
      }
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      // Mesmo `errorFields` é reaproveitado pelas 2 chamadas (Anotação e
      // Relatório, se ambas tiverem sido disparadas) — qual delas lançou a
      // exceção não importa para o mapeamento, só o `field` retornado pelo
      // backend. `validations: []` (array vazio) é truthy em JS, então
      // `haveExceptionValidations` sozinho não garante que algo ficará
      // visível — checar explicitamente se o mapeamento marcou algum campo
      // antes de suprimir o Snackbar genérico (sem isso, uma resposta sem
      // validações mapeáveis falharia em silêncio total).
      if (isBadRequestException(exception) && haveExceptionValidations(exception)) {
        const [atualizados, naoMapeados] = updateFieldsWithErrors(exception, makeErrorFieldsIniciais());
        const temErroVisivel = Object.values(atualizados).some(campo => campo.error);
        if (temErroVisivel) {
          setErrorFields(atualizados);
          // Validações cujo `field` não corresponde a nenhuma chave de
          // `errorFields` (achado em code review) não podem desaparecer em
          // silêncio só porque OUTRA validação do mesmo 400 já foi mapeada
          // para um campo visível.
          if (naoMapeados.length > 0) {
            setErro(naoMapeados.map(item => item.message).join(' '));
          }
          return;
        }
      }
      setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  }

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy, mb: 1 }}>
        Bloco de Anotação
      </Typography>

      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <Field
            tipo="select"
            label="Tipo do Contato/Recado"
            options={tipoProcessoContatos}
            value={campos.tipoProcessoContatoSelecionado}
            onChange={setCampo('tipoProcessoContatoSelecionado')}
            onFocus={limparErro('tipoProcessoContatoSelecionado')}
            error={errorFields.tipoProcessoContatoSelecionado.error}
            helperText={errorFields.tipoProcessoContatoSelecionado.errorMessage}
            disabled={enviando}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            label="Nome do contato"
            value={campos.nomeContatoRecado}
            onChange={setCampo('nomeContatoRecado')}
            onFocus={limparErro('nomeContatoRecado')}
            error={errorFields.nomeContatoRecado.error}
            helperText={errorFields.nomeContatoRecado.errorMessage}
            disabled={enviando}
          />
        </Grid>
        <Grid item xs={3}>
          <Field
            label="DDD"
            value={campos.ddd}
            onChange={setCampo('ddd')}
            onFocus={limparErro('ddd')}
            error={errorFields.ddd.error}
            helperText={errorFields.ddd.errorMessage}
            disabled={enviando}
          />
        </Grid>
        <Grid item xs={3}>
          <Field
            label="Telefone"
            value={campos.telefone}
            onChange={setCampo('telefone')}
            onFocus={limparErro('telefone')}
            error={errorFields.telefone.error}
            helperText={errorFields.telefone.errorMessage}
            disabled={enviando}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            tipo="select"
            label="Tipo do Histórico*"
            options={tipoHistoricoSelectorItems}
            value={campos.idTipoHistorico}
            onChange={setCampo('idTipoHistorico')}
            onFocus={limparErro('idTipoHistorico')}
            error={errorFields.idTipoHistorico.error}
            helperText={errorFields.idTipoHistorico.errorMessage}
            disabled={enviando}
          />
        </Grid>
        {ehGrupoElfa && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy, mb: 1 }}>
              Ocorrência
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Field
                  tipo="select"
                  label="Código da Ocorrência"
                  options={ocorrenciasDisponiveis}
                  value={codOcorrencia}
                  onChange={onSelectOcorrencia}
                  disabled={enviando}
                />
              </Grid>
              {codOcorrencia !== '' && (
                <Grid item xs={12}>
                  <Field
                    label={`Complemento${complementoObrigatorio ? '*' : ''}`}
                    multiline
                    minRows={3}
                    placeholder="Observações adicionais (máx. 400 caracteres)"
                    inputProps={{ maxLength: 400 }}
                    value={complemento}
                    onChange={event => setComplemento(event.target.value)}
                    disabled={enviando}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Field
            label="Anotação"
            multiline
            minRows={4}
            value={campos.textoAnotacao}
            onChange={onChangeTextoAnotacao}
            onFocus={limparErro('texto')}
            error={errorFields.texto.error}
            helperText={errorFields.texto.errorMessage}
            disabled={enviando}
          />
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
            onClick={() => setTextoPadraoAberto('anotacao')}
            disabled={enviando}
          >
            Texto Padrão
          </Button>
          <Tooltip title={DICA_MINIMO_PALAVRAS}>
            <span>
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, ml: 1 }}
                onClick={() => setReescreverAberto('anotacao')}
                disabled={enviando || !podeReescreverAnotacao}
              >
                Reescrever
              </Button>
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Field
            label="Relatório para Cliente"
            multiline
            minRows={3}
            value={campos.textoRelatorio}
            onChange={onChangeTextoRelatorio}
            onFocus={limparErro('texto')}
            error={errorFields.texto.error}
            helperText={errorFields.texto.errorMessage}
            disabled={enviando}
          />
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
            onClick={() => setTextoPadraoAberto('relatorio')}
            disabled={enviando}
          >
            Texto Padrão
          </Button>
          <Tooltip title={DICA_MINIMO_PALAVRAS}>
            <span>
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, ml: 1 }}
                onClick={() => setReescreverAberto('relatorio')}
                disabled={enviando || !podeReescreverRelatorio}
              >
                Reescrever
              </Button>
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setAntecedentesDialogAberto(true)}
            disabled={enviando || antecedentesAtivos == null}
          >
            Antecedentes
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!podeSubmeter || enviando || !podeAnotarNaFase}
          >
            Anotar
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErro('')}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>

      {/*
        Snackbar separado de `erro` de propósito: este é usado só pelo catch
        de `buscarOcorrenciasGrupoElfa` (falha de rede transitória e não
        relacionada a nenhuma ação do usuário) — reaproveitar o Snackbar de
        `erro` misturaria uma falha de fetch em background com uma falha de
        submit real.
      */}
      <Snackbar
        open={Boolean(avisoNaoBloqueante)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setAvisoNaoBloqueante('')}
      >
        <Alert severity="warning" variant="filled" onClose={() => setAvisoNaoBloqueante('')}>
          {avisoNaoBloqueante}
        </Alert>
      </Snackbar>

      <DialogTextoPadrao
        open={textoPadraoAberto === 'anotacao'}
        tipos={TIPOS_TEXTO_PADRAO_ANOTACAO}
        onClose={() => setTextoPadraoAberto(null)}
        onConfirmar={concatenarTextoPadrao('textoAnotacao')}
      />
      <DialogTextoPadrao
        open={textoPadraoAberto === 'relatorio'}
        tipos={TIPOS_TEXTO_PADRAO_RELATORIO}
        onClose={() => setTextoPadraoAberto(null)}
        onConfirmar={concatenarTextoPadrao('textoRelatorio')}
      />

      <DialogReescrever
        open={reescreverAberto === 'anotacao'}
        texto={campos.textoAnotacao}
        onClose={() => setReescreverAberto(null)}
        onAceitar={substituirTexto('textoAnotacao')}
      />
      <DialogReescrever
        open={reescreverAberto === 'relatorio'}
        texto={campos.textoRelatorio}
        onClose={() => setReescreverAberto(null)}
        onAceitar={substituirTexto('textoRelatorio')}
      />

      <DialogSelecionarAntecedentes
        open={antecedentesDialogAberto}
        antecedentes={antecedentesAtivos}
        selecionados={antecedentesSelecionados}
        onToggle={onToggleAntecedente}
        onSelecionarTodos={onSelecionarTodosAntecedentes}
        onClose={() => setAntecedentesDialogAberto(false)}
        onCancelar={onCancelarSelecaoAntecedentes}
        mensagem={MENSAGEM_SELECIONAR_ANTECEDENTES}
      />
    </Box>
  );
}
BlocoAnotacao.propTypes = {
  processo: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  sinalizacaoInfo: PropTypes.shape({
    idFase: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  antecedentesAtivos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    numero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    negociador: PropTypes.string,
    fase: PropTypes.string,
  })),
  tiposHistoricoOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string,
  })),
  onSucesso: PropTypes.func,
  deveResetarCampos: PropTypes.bool.isRequired,
};
BlocoAnotacao.defaultProps = {
  processo: null,
  sinalizacaoInfo: null,
  antecedentesAtivos: null,
  tiposHistoricoOptions: [],
  onSucesso: null,
};
